// Constantes pour les sélecteurs DOM fréquemment utilisés
const SELECTORS = {
  CART_ITEMS: '#main-cart-items, #CartDrawer-CartItems',
  CART_ERRORS: '#cart-errors, #CartDrawer-CartErrors',
  CART_DRAWER: 'cart-drawer',
  LOADING_SPINNER: '.loading__spinner',
  CART_ITEM_NAME: '.cart-item__name, .cart-item-new__name',
};

// Fonctions utilitaires
function getSectionInnerHTMLSafe(html, selector) {
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
}

function toggleCartEmptyState(cartItems, isEmpty) {
  // Mettre à jour l'état du panier (vide/non-vide)
  if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);

  const cartDrawerWrapper = document.querySelector(SELECTORS.CART_DRAWER);
  const cartFooter = document.getElementById('main-cart-footer');

  if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
  if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);
}

function handleCartErrors(error, customMessage) {
  console.error('Erreur panier:', error);
  const errors = document.querySelector(SELECTORS.CART_ERRORS);
  if (errors) {
    errors.textContent = customMessage || (window.cartStrings ? window.cartStrings.error : 'Une erreur est survenue');
  }
}

function safePublishEvent(eventName, data) {
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS[eventName], data);
  }
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

      // Activer l'état de chargement pour l'item
      this.setItemLoadingState(true);

      if (this.dataset.groupType === 'bundle' && event.currentTarget.querySelector('button').dataset.itemKeys) {
        // Vérifier si c'est un bundle avec plusieurs articles à supprimer
        const itemKeys = event.currentTarget
          .querySelector('button')
          .dataset.itemKeys.split(',')
          .filter((key) => key.trim());
        // Supprimer tous les articles du bundle en utilisant leurs clés uniques
        this.removeAllBundleItems(cartItems, itemKeys);
      } else {
        // Comportement normal pour les autres articles
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }

  /**
   * Supprime tous les articles d'un bundle en utilisant leurs clés uniques
   * @param {HTMLElement} cartItems - L'élément du panier contenant les articles
   * @param {Array} itemKeys - Tableau de clés d'articles à supprimer
   */
  removeAllBundleItems(cartItems, itemKeys) {
    // Identifier l'élément de bundle pour le loading
    const bundleItem = this.closest('.cart-item--bundle, .cart-item-new--bundle');

    // Activer le spinner de chargement
    this.enableBundleLoading(bundleItem);

    // Préparer l'objet updates avec les clés valides
    const updates = this._prepareItemUpdates(itemKeys);

    // Préparer la requête
    const sections = this._getSectionsForRequest(cartItems);
    const body = JSON.stringify({ updates, sections });

    // Envoyer la requête
    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => this._handleBundleRemovalSuccess(cartItems, state))
      .catch((error) => handleCartErrors(error))
      .finally(() => this.disableBundleLoading(bundleItem));
  }

  /**
   * Prépare un objet updates avec les clés d'articles valides
   * @private
   */
  _prepareItemUpdates(itemKeys) {
    const updates = {};
    itemKeys.forEach((key) => {
      if (key && key.trim()) {
        updates[key] = 0; // Mettre la quantité à zéro
      }
    });
    return updates;
  }

  /**
   * Récupère les sections à mettre à jour dans la requête
   * @private
   */
  _getSectionsForRequest(cartItems) {
    return cartItems.getSectionsToRender ? cartItems.getSectionsToRender().map((section) => section.section) : [];
  }

  /**
   * Met à jour les sections DOM avec les nouvelles données
   * @private
   */
  _updateDOMSections(cartItems, parsedState) {
    if (!cartItems.getSectionsToRender) return;

    cartItems.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

      // Obtenir le contenu HTML
      const htmlContent =
        typeof cartItems.getSectionInnerHTML === 'function'
          ? cartItems.getSectionInnerHTML(parsedState.sections[section.section], section.selector)
          : getSectionInnerHTMLSafe(parsedState.sections[section.section], section.selector);

      elementToReplace.innerHTML = htmlContent;
    });
  }

  /**
   * Gère le focus après la suppression des articles
   * @private
   */
  _handleFocusAfterRemoval(isEmpty) {
    if (typeof trapFocus !== 'function') return;

    const cartDrawerWrapper = document.querySelector(SELECTORS.CART_DRAWER);
    if (!cartDrawerWrapper) return;

    if (isEmpty) {
      const emptyDrawer = cartDrawerWrapper.querySelector('.drawer__inner-empty');
      const firstLink = cartDrawerWrapper.querySelector('a');
      if (emptyDrawer && firstLink) {
        trapFocus(emptyDrawer, firstLink);
      }
    } else if (document.querySelector('.cart-item, .cart-item-new')) {
      trapFocus(cartDrawerWrapper, document.querySelector(SELECTORS.CART_ITEM_NAME));
    }
  }

  /**
   * Gère la réponse réussie de la suppression du bundle
   * @private
   */
  _handleBundleRemovalSuccess(cartItems, state) {
    const parsedState = JSON.parse(state);

    // Vérifier s'il y a des erreurs
    if (parsedState.errors) {
      handleCartErrors(null, parsedState.errors);
      return;
    }

    // Mettre à jour l'interface
    toggleCartEmptyState(cartItems, parsedState.item_count === 0);

    // Mettre à jour les sections DOM
    this._updateDOMSections(cartItems, parsedState);

    // Gérer le focus
    this._handleFocusAfterRemoval(parsedState.item_count === 0);

    // Publier l'événement de mise à jour
    safePublishEvent('cartUpdate', {
      source: 'cart-items',
      cartData: parsedState,
    });
  }

  /**
   * Active le chargement sur le bundle
   */
  enableBundleLoading(bundleItem) {
    if (!bundleItem) return;

    // Désactiver l'interaction avec le panier
    const mainCartItems = document.querySelector(SELECTORS.CART_ITEMS);
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    // Afficher le spinner sur l'élément du bundle
    const spinner = bundleItem.querySelector(SELECTORS.LOADING_SPINNER);
    if (spinner) {
      spinner.classList.remove('hidden');
    }

    // Masquer les erreurs précédentes
    const errors = document.querySelector(SELECTORS.CART_ERRORS);
    if (errors) {
      errors.textContent = '';
    }

    document.activeElement.blur();
  }

  /**
   * Désactive le chargement sur le bundle
   */
  disableBundleLoading(bundleItem) {
    // Réactiver l'interaction avec le panier
    const mainCartItems = document.querySelector(SELECTORS.CART_ITEMS);
    if (mainCartItems) {
      setTimeout(() => {
        mainCartItems.classList.remove('cart__items--disabled');
      }, 500);
    }

    // Masquer le spinner si présent
    if (bundleItem) {
      const spinner = bundleItem.querySelector(SELECTORS.LOADING_SPINNER);
      if (spinner) {
        spinner.classList.add('hidden');
      }
    }

    // Désactiver l'état de chargement pour l'item
    this.setItemLoadingState(false);
  }

  setItemLoadingState(isLoading) {
    const cartItem = this.closest('.cart-item-new');
    if (!cartItem) return;

    // Ajouter/enlever la classe loading au bouton de suppression
    const removeBtn = this.querySelector('.cart-item-new__remove-btn');
    if (removeBtn) {
      if (isLoading) {
        removeBtn.classList.add('loading');
        const spinner = removeBtn.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.remove('hidden');
        }
      } else {
        removeBtn.classList.remove('loading');
        const spinner = removeBtn.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.add('hidden');
        }
      }
    }

    // Désactiver/activer les boutons de quantité
    const quantityButtons = cartItem.querySelectorAll('.cart-item-new__quantity-btn');
    quantityButtons.forEach((btn) => {
      btn.disabled = isLoading;
    });

    // Désactiver/activer l'input de quantité
    const quantityInput = cartItem.querySelector('.cart-item-new__quantity-input');
    if (quantityInput) {
      quantityInput.disabled = isLoading;
    }

    // Désactiver/activer les boutons d'options d'achat
    const optionButtons = cartItem.querySelectorAll('.cart-item-new__option-btn');
    optionButtons.forEach((button) => {
      button.disabled = isLoading;
      if (isLoading) {
        button.style.opacity = '0.5';
      } else {
        button.style.opacity = '';
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartSubscriptionToggle extends HTMLElement {
  constructor() {
    super();

    this.cart = document.querySelector('cart-drawer');

    this.init();
  }

  init() {
    // Écouter les clics sur les boutons d'options d'achat
    this.addEventListener('click', this.handleOptionClick.bind(this));
  }

  handleOptionClick(event) {
    const button = event.target.closest('.cart-item-new__option-btn');
    if (!button) return;

    event.preventDefault();

    const purchaseType = button.dataset.purchaseType;
    const itemKey = button.dataset.itemKey;
    const variantId = button.dataset.variantId;
    const sellingPlanId = button.dataset.sellingPlanId;
    const isBundle = this.dataset.isBundle === 'true';

    // Activer l'état de chargement pour l'item
    this.setItemLoadingState(true);

    // Récupérer la quantité actuelle de l'item
    const currentItem = document.querySelector(`[data-item-key="${itemKey}"]`);
    const quantity = currentItem
      ? parseInt(currentItem.querySelector('.cart-item-new__quantity-display')?.textContent || '1')
      : 1;

    // Effectuer la mise à jour du panier selon le type (bundle ou produit normal)
    if (isBundle) {
      console.log(
        `Mise à jour du bundle: ${purchaseType}, itemKey: ${itemKey}, variantId: ${variantId}, sellingPlanId: ${sellingPlanId}, quantity: ${quantity}`,
      );
      this.handleBundleToggle({ purchaseType, itemKey, variantId, sellingPlanId, quantity })
        .then(() => {
          // Mettre à jour l'interface utilisateur
          this.updateUI(button);
        })
        .catch((error) => {
          console.error('Erreur lors de la mise à jour du bundle:', error);
          this.showError('Une erreur est survenue lors de la mise à jour du bundle');
        })
        .finally(() => {
          this.setItemLoadingState(false);
        });
    } else {
      this.updateCartItem({ purchaseType, itemKey, variantId, sellingPlanId, quantity })
        .then(() => {
          // Mettre à jour l'interface utilisateur
          this.updateUI(button);
        })
        .catch((error) => {
          console.error("Erreur lors de la mise à jour de l'abonnement:", error);
          this.showError('Une erreur est survenue lors de la mise à jour');
        })
        .finally(() => {
          this.setItemLoadingState(false);
        });
    }
  }

  async updateBundleQuantity(updateData) {
    const { newQuantity } = updateData;

    try {
      // Récupérer les informations du bundle
      const bundleInfo = this.getBundleInfoFromScript();
      if (!bundleInfo) {
        throw new Error('Impossible de récupérer les informations du bundle');
      }

      if (newQuantity === 0) {
        // Si quantité = 0, supprimer complètement le bundle
        await this.removeBundleFromCart(bundleInfo);
      } else {
        // Sinon, mettre à jour la quantité
        await this.updateBundleQuantityInCart(bundleInfo, newQuantity);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la quantité du bundle:', error);
      throw error;
    }
  }

  async updateBundleQuantityInCart(bundleInfo, newQuantity) {
    try {
      // Étape 1 : Supprimer le bundle existant
      await this.removeBundleFromCart(bundleInfo, true);

      // Étape 2 : Relire la structure de base depuis le DOM (toujours non corrompue)
      const baseBundleInfo = this.getBundleInfoFromScript();

      // Étape 3 : Mettre à jour bundleQuantity dans bundleInfo
      const updatedBundleInfo = {
        ...baseBundleInfo,
        _bundleQuantity: newQuantity,
      };

      // Étape 4 : Réajouter avec la nouvelle quantité
      const isSubscription = updatedBundleInfo._bundleFrequency === 'subscription';
      console.log('bundleInfo', updatedBundleInfo, updatedBundleInfo._bundleFrequency, isSubscription, newQuantity);

      if (isSubscription) {
        await this.addBundleAsSubscriptionWithQuantity(updatedBundleInfo, newQuantity);
      } else {
        await this.addBundleAsOneTimeWithQuantity(updatedBundleInfo, newQuantity);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la quantité:', error);
      throw error;
    }
  }

  async addBundleAsSubscriptionWithQuantity(bundleInfo, bundleQuantity) {
    try {
      // Utiliser directement les informations du bundleInfo
      const boxId = bundleInfo._boxId;
      const boxSizeId = bundleInfo._boxSizeId;
      const discountId = bundleInfo._discountId;
      const sellingPlanId = bundleInfo._bundleSellingPlan;

      // Utiliser directement les productVariants du bundleInfo
      let baseProductVariants = bundleInfo._productVariants || [];

      // Si c'est un array contenant un array, extraire le premier élément
      if (
        Array.isArray(baseProductVariants) &&
        baseProductVariants.length === 1 &&
        Array.isArray(baseProductVariants[0])
      ) {
        baseProductVariants = baseProductVariants[0];
      }

      // Convertir l'objet en array si nécessaire (cas où les données sont stockées comme {0: {...}, 1: {...}})
      if (baseProductVariants && typeof baseProductVariants === 'object' && !Array.isArray(baseProductVariants)) {
        baseProductVariants = Object.values(baseProductVariants).filter((variant) => variant && variant.shopifyId);
      }

      // Multiplier les quantités par le nombre de bundles
      const productVariants = baseProductVariants.map((variant) => {
        return {
          ...variant,
          quantity: variant.quantity * bundleQuantity, // Multiplier la quantité par le nombre de bundles
        };
      });

      console.log('Product variants avec qtys multipliées : ', productVariants);

      // Préparer le payload pour la transaction Loop avec la nouvelle quantité
      const payload = {
        boxSizeId: boxSizeId,
        sellingPlanShopifyId: parseInt(sellingPlanId),
        productVariants: baseProductVariants, // Quantités de base pour Loop
        quantity: bundleQuantity, // Quantité de bundles
      };

      // Ajouter le discount si disponible
      if (discountId) {
        payload.discountId = discountId;
      }

      // Ajouter l'ID client si disponible
      if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
        payload.shopify_customer_id = window.Shopify.customer.id.toString();
      }

      console.log('Payload envoyé à Loop API (subscription avec quantité):', payload);

      // Créer la transaction Loop
      const response = await fetch(`https://api.loopsubscriptions.com/storefront/2023-10/bundle/${boxId}/transaction`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Transaction failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success || !data.data?.txnId) {
        throw new Error('No transaction ID received from Loop API');
      }

      const txnId = data.data.txnId;

      // Ajouter les produits au panier Shopify avec les quantités multipliées
      await this.addToCartWithBundleId(
        productVariants, // Quantités multipliées pour Shopify
        baseProductVariants, // Quantités de base pour properties
        txnId,
        parseInt(sellingPlanId),
        boxId,
        { boxSizeId, discountId },
        bundleInfo,
      );
    } catch (error) {
      console.error("Erreur lors de l'ajout en subscription avec quantité:", error);
      throw error;
    }
  }

  async addBundleAsOneTimeWithQuantity(bundleInfo, bundleQuantity) {
    try {
      // Utiliser directement les informations du bundleInfo
      const boxId = bundleInfo._boxId;
      const boxSizeId = bundleInfo._boxSizeId;
      const discountId = bundleInfo._discountId;

      // Utiliser directement les productVariants du bundleInfo
      let baseProductVariants = bundleInfo._productVariants || [];

      // Si c'est un array contenant un array, extraire le premier élément
      if (
        Array.isArray(baseProductVariants) &&
        baseProductVariants.length === 1 &&
        Array.isArray(baseProductVariants[0])
      ) {
        baseProductVariants = baseProductVariants[0];
      }

      // Convertir l'objet en array si nécessaire (cas où les données sont stockées comme {0: {...}, 1: {...}})
      if (baseProductVariants && typeof baseProductVariants === 'object' && !Array.isArray(baseProductVariants)) {
        baseProductVariants = Object.values(baseProductVariants).filter((variant) => variant && variant.shopifyId);
      }

      // Multiplier les quantités par le nombre de bundles
      const productVariants = baseProductVariants.map((variant) => ({
        ...variant,
        quantity: variant.quantity * bundleQuantity,
      }));

      console.log('ProductVariants avec quantité multipliée:', productVariants);

      // Préparer le payload pour la transaction Loop avec la nouvelle quantité
      const payload = {
        boxSizeId: boxSizeId,
        sellingPlanShopifyId: null,
        productVariants: baseProductVariants, // Quantités de base pour Loop
        quantity: bundleQuantity, // Quantité de bundles
      };

      // Ajouter le discount si disponible
      if (discountId) {
        payload.discountId = discountId;
      }

      // Ajouter l'ID client si disponible
      if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
        payload.shopify_customer_id = window.Shopify.customer.id.toString();
      }

      console.log('Payload envoyé à Loop API (one-time avec quantité):', payload);

      // Créer la transaction Loop
      const response = await fetch(`https://api.loopsubscriptions.com/storefront/2023-10/bundle/${boxId}/transaction`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Transaction failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success || !data.data?.txnId) {
        throw new Error('No transaction ID received from Loop API');
      }

      const txnId = data.data.txnId;

      // Ajouter les produits au panier Shopify avec les quantités multipliées
      await this.addToCartWithBundleIdOneTime(
        productVariants, // Quantités multipliées pour Shopify
        baseProductVariants, // Quantités de base pour properties
        txnId,
        boxId,
        { boxSizeId, discountId },
        bundleInfo,
      );
    } catch (error) {
      console.error("Erreur lors de l'ajout en one-time avec quantité:", error);
      throw error;
    }
  }

  async handleBundleToggle(updateData) {
    const { purchaseType } = updateData;

    try {
      // Étape 1 : Récupérer les informations du bundle depuis le script
      const bundleInfo = this.getBundleInfoFromScript();
      if (!bundleInfo) {
        throw new Error('Impossible de récupérer les informations du bundle');
      }

      // Récupérer le selling_plan depuis le bouton cliqué
      const activeButton =
        this.querySelector('.cart-item-new__option-btn.active') ||
        this.querySelector('.cart-item-new__option-btn[data-purchase-type="' + purchaseType + '"]');
      const sellingPlanId = activeButton ? activeButton.dataset.sellingPlanId : null;

      // Étape 2 : Retirer tous les produits du bundle du panier
      await this.removeBundleFromCart(bundleInfo, true);

      // Étape 3 : Réajouter les produits avec ou sans selling_plan
      if (purchaseType === 'subscription') {
        await this.addBundleAsSubscription(bundleInfo, sellingPlanId);
      } else {
        await this.addBundleAsOneTime(bundleInfo);
      }

      // Étape 4 : Recharger le sidecart
      // (inutile, le rendu est déjà géré par renderContents(cartData))
    } catch (error) {
      console.error('Erreur lors du basculement du bundle:', error);
      throw error;
    }
  }
  async updateCartItem(updateData) {
    const { purchaseType, itemKey, variantId, sellingPlanId, quantity } = updateData;

    // Étape 1: Supprimer l'item existant
    await this.removeCartItem(itemKey);

    // Étape 2: Ajouter le nouvel item avec ou sans selling plan
    await this.addCartItem(purchaseType, variantId, sellingPlanId, quantity);

    return true;
  }

  async removeCartItem(itemKey) {
    const sections = this.getSectionsToUpdate();
    const updates = {};
    updates[itemKey] = 0;

    const response = await fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates: updates,
        sections: sections,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.text();
    const parsedResult = JSON.parse(result);

    if (parsedResult.errors) {
      throw new Error(parsedResult.errors);
    }

    return parsedResult;
  }

  async addCartItem(purchaseType, variantId, sellingPlanId, quantity) {
    const sections = this.getSectionsToUpdate();

    // Préparer les données pour l'ajout
    const formData = {
      items: [
        {
          id: variantId,
          quantity: quantity,
        },
      ],
      sections: sections,
    };

    // Ajouter le selling_plan_id si c'est un abonnement
    if (purchaseType === 'subscription' && sellingPlanId) {
      formData.items[0].selling_plan = sellingPlanId;
    }

    const response = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.text();
    const parsedResult = JSON.parse(result);

    if (parsedResult.errors) {
      throw new Error(parsedResult.errors);
    }

    // Mettre à jour les sections du DOM
    this.updateDOMSections(parsedResult);

    return parsedResult;
  }

  getSectionsToUpdate() {
    const cartItems = document.querySelector('cart-drawer-items');
    if (cartItems && cartItems.getSectionsToRender) {
      return cartItems.getSectionsToRender().map((section) => section.section);
    }
    return ['cart-drawer'];
  }

  updateDOMSections(parsedState) {
    const cartItems = document.querySelector('cart-drawer-items');
    if (!cartItems || !cartItems.getSectionsToRender) return;

    cartItems.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);

      if (elementToReplace && parsedState.sections && parsedState.sections[section.section]) {
        const htmlContent = getSectionInnerHTMLSafe(parsedState.sections[section.section], section.selector);
        elementToReplace.innerHTML = htmlContent;
      }
    });

    // Publier l'événement de mise à jour du panier
    this.publishCartUpdate(parsedState);
  }

  updateUI(clickedButton) {
    const container = clickedButton.closest('.cart-item-new__purchase-options');
    if (!container) return;

    // Retirer la classe active de tous les boutons
    container.querySelectorAll('.cart-item-new__option-btn').forEach((btn) => {
      btn.classList.remove('active');
    });

    // Ajouter la classe active au bouton cliqué
    clickedButton.classList.add('active');
  }

  setItemLoadingState(isLoading) {
    const cartItem = this.closest('.cart-item-new');
    if (!cartItem) return;

    // Désactiver/activer les boutons d'options d'achat
    const optionButtons = this.querySelectorAll('.cart-item-new__option-btn');
    optionButtons.forEach((button) => {
      button.disabled = isLoading;
      if (isLoading) {
        button.style.opacity = '0.5';
      } else {
        button.style.opacity = '';
      }
    });

    // Désactiver/activer les boutons de quantité
    const quantityButtons = cartItem.querySelectorAll('.cart-item-new__quantity-btn');
    quantityButtons.forEach((btn) => {
      btn.disabled = isLoading;
    });

    // Désactiver/activer l'input de quantité
    const quantityInput = cartItem.querySelector('.cart-item-new__quantity-input');
    if (quantityInput) {
      quantityInput.disabled = isLoading;
    }

    // Ajouter/enlever la classe loading au bouton de suppression
    const removeBtn = cartItem.querySelector('.cart-item-new__remove-btn');
    if (removeBtn) {
      if (isLoading) {
        removeBtn.classList.add('loading');
        const spinner = removeBtn.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.remove('hidden');
        }
      } else {
        removeBtn.classList.remove('loading');
        const spinner = removeBtn.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.add('hidden');
        }
      }
    }
  }

  showError(message) {
    // Afficher l'erreur dans la zone d'erreurs du panier
    const errorContainer = document.querySelector('#CartDrawer-CartErrors');
    if (errorContainer) {
      errorContainer.textContent = message;
      setTimeout(() => {
        errorContainer.textContent = '';
      }, 5000);
    }
  }

  publishCartUpdate(cartData) {
    safePublishEvent('cartUpdate', {
      source: 'cart-subscription-toggle',
      cartData: cartData,
    });
  }

  getBundleInfoFromScript(itemKey) {
    // Trouver l'élément cart-item-new qui contient cet itemKey
    const cartItem = this.closest('.cart-item-new');
    if (!cartItem) {
      console.error('Cart item non trouvé pour:', itemKey);
      return null;
    }

    // Récupérer le script dans cet élément cart-item-new
    const bundleScript = cartItem.querySelector('script');
    if (!bundleScript) {
      console.error('Script bundle non trouvé dans cart-item-new pour:', itemKey);
      return null;
    }

    try {
      // Nettoyer le JSON pour corriger les virgules en trop
      let jsonContent = bundleScript.textContent.trim();

      // Corriger les virgules en trop dans les tableaux : [, -> [
      // jsonContent = jsonContent.replace(/\[\s*,/g, '[');

      // Corriger les virgules multiples : ,, -> ,
      // jsonContent = jsonContent.replace(/,\s*,/g, ',');

      const bundleData = JSON.parse(jsonContent);

      console.log('Données du bundle récupérées:', bundleData);

      // Récupérer les itemKeys depuis le JSON et filtrer les valeurs vides
      const itemKeys = (bundleData.itemKeys || []).filter((key) => key && key.trim());

      // console.log('ItemKeys récupérés:', itemKeys);

      const parsedProductVariants = typeof bundleData._productVariants === 'string'
        ? JSON.parse(bundleData._productVariants.replace(/&quot;/g, '"'))
        : bundleData._productVariants;
      // console.log('Parsed _productVariants:', parsedProductVariants);

      return {
        _bundleId: bundleData._bundleId,
        _bundleSize: bundleData._bundleSize,
        _boxId: bundleData._boxId || '01K0Y98PT8AQPKQ8WQKG694J9F',
        _boxSizeId: bundleData._boxSizeId,
        _bundleFrequency: bundleData._bundleFrequency || 'subscription',
        _bundleQuantity: bundleData._bundleQuantity || 1,
        _discountId: bundleData._discountId,
        _bundleSellingPlan: bundleData._bundleSellingPlan,
        _productVariants: parsedProductVariants,
        itemKeys: itemKeys,
      };
    } catch (error) {
      console.error('Erreur lors du parsing des données bundle:', error);
      console.error('Contenu JSON:', bundleScript.textContent);
      return null;
    }
  }

  async removeBundleFromCart(bundleInfo, isToggling = false) {
    try {
      // Utiliser les itemKeys récupérées depuis le script
      const itemKeys = bundleInfo.itemKeys;

      if (!itemKeys || itemKeys.length === 0) {
        console.warn('Aucune itemKey trouvée dans bundleInfo');
        return;
      }

      // Préparer les updates pour supprimer tous les items du bundle
      const updates = {};
      itemKeys.forEach((key) => {
        if (key && key.trim()) {
          updates[key] = 0;
        }
      });

      let body = { updates };
      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(document.activeElement);
        }
      }

      // Supprimer les items du panier
      const removeResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!removeResponse.ok) {
        throw new Error('Erreur lors de la suppression du bundle');
      }

      const parsedResponse = await removeResponse.json();

      if (parsedResponse.items.length == 0 && !isToggling) {
        this.cart.renderContents(parsedResponse);
        return;
      }

      console.log('Bundle supprimé du panier avec les clés:', itemKeys);
    } catch (error) {
      console.error('Erreur lors de la suppression du bundle:', error);
      throw error;
    }
  }

  async addBundleAsSubscription(bundleInfo, sellingPlanId = null) {
    try {
      // Utiliser directement les informations du bundleInfo
      const boxId = bundleInfo._boxId;
      const boxSizeId = bundleInfo._boxSizeId;
      const discountId = bundleInfo._discountId;
      const finalSellingPlanId = sellingPlanId || bundleInfo._bundleSellingPlan;

      // Utiliser directement les productVariants du bundleInfo
      let productVariants = bundleInfo._productVariants || [];

      // Si c'est un array contenant un array, extraire le premier élément
      if (Array.isArray(productVariants) && productVariants.length === 1 && Array.isArray(productVariants[0])) {
        productVariants = productVariants[0];
      }

      // Convertir l'objet en array si nécessaire (cas où les données sont stockées comme {0: {...}, 1: {...}})
      if (productVariants && typeof productVariants === 'object' && !Array.isArray(productVariants)) {
        productVariants = Object.values(productVariants).filter((variant) => variant && variant.shopifyId);
      }

      console.log('ProductVariants du bundleInfo:', productVariants);

      // Préparer le payload pour la transaction Loop
      const payload = {
        boxSizeId: boxSizeId,
        sellingPlanShopifyId: parseInt(finalSellingPlanId),
        productVariants: productVariants,
        quantity: 1,
      };

      // Ajouter le discount si disponible
      if (discountId) {
        payload.discountId = discountId;
      }

      // Ajouter l'ID client si disponible
      if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
        payload.shopify_customer_id = window.Shopify.customer.id.toString();
      }

      console.log('Payload envoyé à Loop API (subscription):', payload);

      // Créer la transaction Loop
      const response = await fetch(`https://api.loopsubscriptions.com/storefront/2023-10/bundle/${boxId}/transaction`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Transaction failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success || !data.data?.txnId) {
        throw new Error('No transaction ID received from Loop API');
      }

      const txnId = data.data.txnId;

      // Ajouter les produits au panier Shopify avec le bundleId
      await this.addToCartWithBundleId(
        payload.productVariants,
        payload.productVariants, // Même chose pour les quantités de base car pas de multiplication ici
        txnId,
        parseInt(finalSellingPlanId),
        boxId,
        { boxSizeId, discountId },
        bundleInfo,
      );
    } catch (error) {
      console.error("Erreur lors de l'ajout en subscription:", error);
      throw error;
    }
  }

  async addBundleAsOneTime(bundleInfo) {
    try {
      // Utiliser directement les informations du bundleInfo
      const boxId = bundleInfo._boxId;
      const boxSizeId = bundleInfo._boxSizeId;
      const discountId = bundleInfo._discountId;
      const bundleQuantity = bundleInfo._bundleQuantity;

      // Utiliser directement les productVariants du bundleInfo
      let productVariants = bundleInfo._productVariants || [];

      // Si c'est un array contenant un array, extraire le premier élément
      if (Array.isArray(productVariants) && productVariants.length === 1 && Array.isArray(productVariants[0])) {
        productVariants = productVariants[0];
      }

      // Convertir l'objet en array si nécessaire (cas où les données sont stockées comme {0: {...}, 1: {...}})
      if (productVariants && typeof productVariants === 'object' && !Array.isArray(productVariants)) {
        productVariants = Object.values(productVariants).filter((variant) => variant && variant.shopifyId);
      }

      // Convertir l'objet en array si nécessaire (cas où les données sont stockées comme {0: {...}, 1: {...}})
      if (productVariants && typeof productVariants === 'object' && !Array.isArray(productVariants)) {
        productVariants = Object.values(productVariants).filter((variant) => variant && variant.shopifyId);
      }

      console.log('ProductVariants du bundleInfo:', productVariants);

      // Préparer le payload pour la transaction Loop (sans selling plan)
      const payload = {
        boxSizeId: boxSizeId,
        sellingPlanShopifyId: null,
        productVariants: productVariants,
        quantity: bundleQuantity || 1,
        discountId: discountId || null,
      };

      console.log('Payload pour la transaction Loop:', payload, boxId);

      // Créer la transaction Loop
      const response = await fetch(`https://api.loopsubscriptions.com/storefront/2023-10/bundle/${boxId}/transaction`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Transaction failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success || !data.data?.txnId) {
        throw new Error('No transaction ID received from Loop API');
      }

      const txnId = data.data.txnId;

      // Ajouter les produits au panier Shopify avec le bundleId (sans selling plan)
      await this.addToCartWithBundleIdOneTime(
        payload.productVariants,
        payload.productVariants, // Même chose pour les quantités de base car pas de multiplication ici
        txnId,
        boxId,
        { boxSizeId, discountId },
        bundleInfo,
      );
    } catch (error) {
      console.error("Erreur lors de l'ajout en one-time:", error);
      throw error;
    }
  }

  async getBundleDetails(boxId) {
    try {
      const response = await fetch(`https://api.loopsubscriptions.com/storefront/2023-10/bundle/${boxId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch bundle details: ${response.status}`);
      }

      const bundleData = await response.json();
      return bundleData.data;
    } catch (error) {
      console.error('Failed to fetch bundle details:', error);
      throw error;
    }
  }

  findBundleConfiguration(bundleDetails, selectedQuantity) {
    // Trouver le boxSize correspondant
    const boxSize = bundleDetails.boxSizes.find((size) => size.minSize === selectedQuantity);
    if (!boxSize) {
      throw new Error(`No box size found for quantity: ${selectedQuantity}`);
    }

    // Trouver le discount correspondant à la quantité
    const discount = bundleDetails.discounts.find(
      (d) => d.minCartQuantity === selectedQuantity && d.purchaseType === 'SUBSCRIPTION',
    );

    return {
      boxSizeId: boxSize.id,
      boxSizeName: boxSize.name,
      discountId: discount?.id,
      discountCode: discount?.code,
    };
  }

  findBundleConfigurationOneTime(bundleDetails, selectedQuantity) {
    // Trouver le boxSize correspondant
    const boxSize = bundleDetails.boxSizes.find((size) => size.minSize === selectedQuantity);
    if (!boxSize) {
      throw new Error(`No box size found for quantity: ${selectedQuantity}`);
    }

    // Trouver le discount correspondant à la quantité pour ONE_TIME
    const discount = bundleDetails.discounts.find(
      (d) => d.minCartQuantity === selectedQuantity && d.purchaseType === 'ONE_TIME',
    );

    return {
      boxSizeId: boxSize.id,
      boxSizeName: boxSize.name,
      discountId: discount?.id,
      discountCode: discount?.code,
    };
  }

  async addToCartWithBundleId(productVariants, baseProductVariants, txnId, sellingPlanId, boxId, config, bundleInfo) {
    try {
      // Préparer les items pour le panier Shopify
      const items = productVariants.map((variant) => ({
        id: variant.shopifyId,
        quantity: variant.quantity,
        selling_plan: sellingPlanId,
        properties: {
          _bundleId: txnId,
          _bundleSize: bundleInfo._bundleSize,
          _bundleQuantity: bundleInfo._bundleQuantity || 1,
          _bundleFrequency: 'subscription',
          _bundleSellingPlan: sellingPlanId.toString(),
          _boxId: boxId,
          _boxSizeId: config.boxSizeId,
          _discountId: config.discountId || '',
          _type: 'Bundle',
          _productVariants: JSON.stringify(baseProductVariants),
        },
      }));

      let body = { items };
      console.log('[debug bundle] Payload envoyé à Shopify:', JSON.stringify(body, null, 2));
      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(document.activeElement);
        }
      }

      // Ajouter au panier via l'API Shopify
      console.log(
        '[debug bundle] Properties de chaque item envoyé à Shopify:',
        items.map((i) => i.properties),
      );
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to add to cart: ${response.status}`);
      }

      const cartData = await response.json();

      if (cartData) {
        this.cart.renderContents(cartData);
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  }

  async addToCartWithBundleIdOneTime(productVariants, baseProductVariants, txnId, boxId, config, bundleInfo) {
    try {
      // Préparer les items pour le panier Shopify (sans selling_plan)
      const items = productVariants.map((variant) => ({
        id: variant.shopifyId,
        quantity: variant.quantity,
        properties: {
          _bundleId: txnId,
          _bundleSize: bundleInfo._bundleSize,
          _bundleQuantity: bundleInfo._bundleQuantity || 1,
          _bundleFrequency: 'onetime',
          _bundleSellingPlan: null,
          _boxId: boxId,
          _boxSizeId: config.boxSizeId,
          _discountId: config.discountId || '',
          _type: 'Bundle',
          _productVariants: JSON.stringify(baseProductVariants),
        },
      }));

      let body = { items };
      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof this.cart.setActiveElement === 'function') {
          this.cart.setActiveElement(document.activeElement);
        }
      }

      // Ajouter au panier via l'API Shopify
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to add to cart: ${response.status}`);
      }

      const cartData = await response.json();

      this.cart.renderContents(cartData);
      return cartData;
    } catch (error) {
      console.error('Failed to add one-time to cart:', error);
      throw error;
    }
  }

  async reloadSideCart() {
    try {
      // Récupérer le panier mis à jour
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      // Publier l'événement de mise à jour du panier
      this.publishCartUpdate(cartData);

      // Mettre à jour le drawer du panier s'il existe
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(cartData);
      }

      // Ou mettre à jour la notification du panier
      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && typeof cartNotification.renderContents === 'function') {
        cartNotification.renderContents(cartData);
      }

      // Mettre à jour les sections du DOM
      this.updateDOMSections({ sections: await this.fetchCartSections() });

      console.log('Sidecart rechargé avec succès');
    } catch (error) {
      console.error('Erreur lors du rechargement du sidecart:', error);
      throw error;
    }
  }

  async fetchCartSections() {
    const sections = this.getSectionsToUpdate();
    const sectionPromises = sections.map(async (sectionId) => {
      const response = await fetch(`/?section_id=${sectionId}`);
      const html = await response.text();
      return { [sectionId]: html };
    });

    const sectionResults = await Promise.all(sectionPromises);
    return sectionResults.reduce((acc, section) => ({ ...acc, ...section }), {});
  }
}

customElements.define('cart-subscription-toggle', CartSubscriptionToggle);

class CartQuantityButtons extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Écouter les clics sur les boutons de quantité
    this.addEventListener('click', this.handleQuantityClick.bind(this));
  }

  handleQuantityClick(event) {
    console.log(event);
    const button = event.target.closest('.cart-item-new__quantity-btn');
    if (!button) return;

    event.preventDefault();

    const action = button.dataset.action;
    const line = button.dataset.line;
    const variantId = button.dataset.variantId;
    const isBundle = button.dataset.isBundle === 'true';

    // Si c'est un bundle, utiliser la logique spéciale

    if (isBundle) {
      this.handleBundleQuantityClick(button, action);
      return;
    }

    // Logique normale pour les produits non-bundle
    // Trouver l'input de quantité correspondant
    const quantityInput = this.querySelector(`#Drawer-quantity-${line}`);
    if (!quantityInput) return;

    const currentQuantity = parseInt(quantityInput.value);
    let newQuantity = currentQuantity;

    // Calculer la nouvelle quantité
    if (action === 'increase') {
      newQuantity = currentQuantity + 1;
    } else if (action === 'decrease') {
      newQuantity = Math.max(0, currentQuantity - 1);
    }

    // Activer l'état de chargement
    this.setLoadingState(true);

    // Mettre à jour l'input et déclencher le changement
    quantityInput.value = newQuantity;

    // Mettre à jour l'affichage visuel
    const displayElement = this.querySelector(`.cart-item-new__quantity-display`);
    if (displayElement) {
      displayElement.textContent = newQuantity;
    }

    // Trouver le conteneur CartItems parent et déclencher la mise à jour
    const cartItems = this.closest('cart-drawer-items');
    if (cartItems && cartItems.updateQuantity) {
      cartItems.updateQuantity(line, newQuantity, button.getAttribute('name'), variantId);
    }
  }

  handleBundleQuantityClick(button, action) {
    // Trouver le composant CartSubscriptionToggle parent
    const cartSubscriptionToggle = this.closest('.cart-item-new').querySelector('cart-subscription-toggle');
    if (!cartSubscriptionToggle) {
      console.error('CartSubscriptionToggle non trouvé pour le bundle');
      return;
    }

    // Récupérer la quantité actuelle
    const quantityDisplay = this.querySelector('.cart-item-new__quantity-display');
    const currentQuantity = parseInt(quantityDisplay?.textContent || '1');

    let newQuantity = currentQuantity;
    if (action === 'increase') {
      newQuantity = currentQuantity + 1;
    } else if (action === 'decrease') {
      newQuantity = Math.max(0, currentQuantity - 1);
    }

    // Activer l'état de chargement
    this.setLoadingState(true);

    // Mettre à jour l'affichage visuel immédiatement
    if (quantityDisplay) {
      quantityDisplay.textContent = newQuantity;
    }

    // Déléguer la logique de mise à jour au CartSubscriptionToggle
    const itemKey = button.dataset.itemKey;
    cartSubscriptionToggle
      .updateBundleQuantity({ itemKey, currentQuantity, newQuantity })
      .then(() => {
        console.log(`Quantité du bundle mise à jour: ${currentQuantity} -> ${newQuantity}`);
      })
      .catch((error) => {
        console.error('Erreur lors de la mise à jour de la quantité du bundle:', error);
        // Remettre l'ancienne quantité en cas d'erreur
        if (quantityDisplay) {
          quantityDisplay.textContent = currentQuantity;
        }
      })
      .finally(() => {
        this.setLoadingState(false);
      });
  }

  setLoadingState(isLoading) {
    const cartItem = this.closest('.cart-item-new');
    if (!cartItem) return;

    // Désactiver/activer les boutons de quantité
    const quantityButtons = this.querySelectorAll('.cart-item-new__quantity-btn');
    quantityButtons.forEach((btn) => {
      btn.disabled = isLoading;
    });

    // Désactiver/activer l'input de quantité
    const quantityInput = this.querySelector('.cart-item-new__quantity-input');
    if (quantityInput) {
      quantityInput.disabled = isLoading;
    }

    // Ajouter/enlever la classe loading au bouton de suppression
    const removeBtn = cartItem.querySelector('.cart-item-new__remove-btn');
    if (removeBtn) {
      if (isLoading) {
        removeBtn.classList.add('loading');
        const spinner = removeBtn.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.remove('hidden');
        }
      } else {
        removeBtn.classList.remove('loading');
        const spinner = removeBtn.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.add('hidden');
        }
      }
    }
  }
}

customElements.define('cart-quantity-buttons', CartQuantityButtons);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  /**
   * Valide la quantité entrée par l'utilisateur
   * @param {Event} event - L'événement de changement
   */
  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    const min = event.target.dataset.min;
    const max = parseInt(event.target.max);
    const step = parseInt(event.target.step);

    // Vérifier les différentes contraintes de quantité
    const message = this._getQuantityValidationMessage(inputValue, min, max, step);

    if (message) {
      // En cas d'erreur, afficher le message et mettre à jour la validité
      this.setValidity(event, index, message);
    } else {
      // Si valide, mettre à jour la quantité
      event.target.setCustomValidity('');
      event.target.reportValidity();

      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  /**
   * Obtient le message de validation de quantité approprié
   * @private
   */
  _getQuantityValidationMessage(value, min, max, step) {
    if (value < min) {
      return window.quickOrderListStrings.min_error.replace('[min]', min);
    }

    if (max && value > max) {
      return window.quickOrderListStrings.max_error.replace('[max]', max);
    }

    if (step && value % step !== 0) {
      return window.quickOrderListStrings.step_error.replace('[step]', step);
    }

    return '';
  }

  /**
   * Gestionnaire d'événements pour le changement de quantité
   */
  onChange(event) {
    this.validateQuantity(event);
  }

  /**
   * Met à jour l'interface du panier quand des changements externes se produisent
   */
  onCartUpdate() {
    // Différentes mises à jour selon le type de panier (tiroir ou page)
    const isCartDrawer = this.tagName === 'CART-DRAWER-ITEMS';
    const sectionId = isCartDrawer ? 'cart-drawer' : 'main-cart-items';

    fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if (isCartDrawer) {
          // Mise à jour du tiroir du panier
          this._updateCartDrawer(html);
        } else {
          // Mise à jour de la page du panier
          this._updateCartPage(html);
        }
      })
      .catch((error) => {
        console.error('Erreur lors de la mise à jour du panier:', error);
      });
  }

  /**
   * Met à jour le tiroir du panier
   * @private
   */
  _updateCartDrawer(html) {
    const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
    selectors.forEach((selector) => {
      const targetElement = document.querySelector(selector);
      const sourceElement = html.querySelector(selector);
      if (targetElement && sourceElement) {
        targetElement.replaceWith(sourceElement);
      }
    });
  }

  /**
   * Met à jour la page du panier
   * @private
   */
  _updateCartPage(html) {
    const sourceQty = html.querySelector('cart-items');
    if (sourceQty) {
      this.innerHTML = sourceQty.innerHTML;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  /**
   * Met à jour la quantité d'un article dans le panier
   * @param {string|number} line - Identifiant de ligne de l'article
   * @param {number} quantity - Nouvelle quantité
   * @param {string} name - Nom de l'élément pour le focus
   * @param {string|number} variantId - ID de la variante
   */
  updateQuantity(line, quantity, name, variantId) {
    // Activer l'indicateur de chargement
    this.enableLoading(line);

    // Préparer et envoyer la requête
    const body = this._prepareQuantityUpdateBody(line, quantity);

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => this._handleQuantityUpdateSuccess(state, line, name, variantId))
      .catch((error) => this._handleQuantityUpdateError(error))
      .finally(() => {
        this.disableLoading(line);
        // Désactiver l'état de chargement pour les boutons de quantité
        const quantityButtons = this.querySelector(`cart-quantity-buttons`);
        if (quantityButtons && quantityButtons.setLoadingState) {
          quantityButtons.setLoadingState(false);
        }
      });
  }

  /**
   * Prépare le corps de la requête de mise à jour de quantité
   * @private
   */
  _prepareQuantityUpdateBody(line, quantity) {
    return JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
  }

  /**
   * Gère la réponse réussie de la mise à jour de quantité
   * @private
   */
  _handleQuantityUpdateSuccess(state, line, name, variantId) {
    const parsedState = JSON.parse(state);
    const quantityElement =
      document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
    const items = document.querySelectorAll('.cart-item');

    // Gérer les erreurs de mise à jour
    if (parsedState.errors) {
      if (quantityElement) {
        quantityElement.value = quantityElement.getAttribute('value');
      }
      this.updateLiveRegions(line, parsedState.errors);
      return;
    }

    // Mettre à jour l'état vide/non-vide
    toggleCartEmptyState(this, parsedState.item_count === 0);

    // Mettre à jour le DOM
    this._updateCartSections(parsedState);

    // Vérifier si la quantité a été modifiée par le serveur
    this._checkQuantityChanges(parsedState, line, quantityElement, items);

    // Gérer le focus
    this._setFocusAfterQuantityUpdate(line, name, parsedState.item_count === 0);

    // Publier l'événement de mise à jour
    safePublishEvent('cartUpdate', {
      source: 'cart-items',
      cartData: parsedState,
      variantId: variantId,
    });
  }

  /**
   * Met à jour les sections du panier avec les données actualisées
   * @private
   */
  _updateCartSections(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

      elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    });
  }

  /**
   * Vérifie si la quantité a été modifiée par le serveur et affiche un message si nécessaire
   * @private
   */
  _checkQuantityChanges(parsedState, line, quantityElement, items) {
    if (!quantityElement) return;

    const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
    let message = '';

    if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
      message =
        typeof updatedValue === 'undefined'
          ? window.cartStrings.error
          : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
    }

    this.updateLiveRegions(line, message);
  }

  /**
   * Définit le focus après une mise à jour de quantité
   * @private
   */
  _setFocusAfterQuantityUpdate(line, name, isEmpty) {
    const cartDrawerWrapper = document.querySelector(SELECTORS.CART_DRAWER);
    if (!cartDrawerWrapper) return;

    const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);

    if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
      if (typeof trapFocus === 'function') {
        cartDrawerWrapper
          ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
          : lineItem.querySelector(`[name="${name}"]`).focus();
      }
    } else if (isEmpty && cartDrawerWrapper.querySelector('.drawer__inner-empty')) {
      if (typeof trapFocus === 'function') {
        trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
      }
    } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
      if (typeof trapFocus === 'function') {
        trapFocus(cartDrawerWrapper, document.querySelector(SELECTORS.CART_ITEM_NAME));
      }
    }
  }

  /**
   * Gère les erreurs de mise à jour de quantité
   * @private
   */
  _handleQuantityUpdateError() {
    this.querySelectorAll(SELECTORS.LOADING_SPINNER).forEach((overlay) => overlay.classList.add('hidden'));
    handleCartErrors(null, window.cartStrings ? window.cartStrings.error : null);
  }
  /**
   * Met à jour les régions live pour l'accessibilité
   * @param {string|number} line - Identifiant de ligne de l'article
   * @param {string} message - Message à afficher
   */
  updateLiveRegions(line, message) {
    // Mettre à jour le message d'erreur spécifique à la ligne
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);

    if (lineItemError && lineItemError.querySelector('.cart-item__error-text')) {
      lineItemError.querySelector('.cart-item__error-text').textContent = message;
    }

    // Gérer l'élément de statut pour l'accessibilité
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'true');
    }

    // Mettre à jour la région live globale
    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');

    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', 'false');
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  /**
   * Extrait le HTML intérieur d'une section
   */
  getSectionInnerHTML(html, selector) {
    return getSectionInnerHTMLSafe(html, selector);
  }

  /**
   * Active l'indicateur de chargement pour un article
   * @param {string|number} line - Identifiant de ligne de l'article
   */
  enableLoading(line) {
    // Désactiver l'interaction avec le panier
    const mainCartItems = document.querySelector(SELECTORS.CART_ITEMS);
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    // Vérifier si c'est un item dans un bundle (dans le details du bundle)
    const bundlePackItem = this.querySelector(`#CartItem-${line}.cart-item__pack-item`);

    if (bundlePackItem) {
      // Pour les items du bundle, afficher le spinner et masquer les contrôles
      const itemSpinner = bundlePackItem.querySelector('.cart-item__pack-item-loading');
      const removeButton = bundlePackItem.querySelector('cart-remove-button');
      const quantityWrapper = bundlePackItem.querySelector('.cart-item__pack-quantity-wrapper');

      if (itemSpinner) {
        itemSpinner.classList.remove('hidden');
      }
      if (removeButton) {
        removeButton.style.visibility = 'hidden';
      }
      if (quantityWrapper) {
        quantityWrapper.style.visibility = 'hidden';
      }
    } else {
      // Afficher les spinners de chargement normaux
      const cartItemElements = this.querySelectorAll(`#CartItem-${line} ${SELECTORS.LOADING_SPINNER}`);
      const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} ${SELECTORS.LOADING_SPINNER}`);

      [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => {
        overlay.classList.remove('hidden');
      });
    }

    // Enlever le focus actif
    document.activeElement.blur();

    // Mettre à jour le statut pour l'accessibilité
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Désactive l'indicateur de chargement pour un article
   * @param {string|number} line - Identifiant de ligne de l'article
   */
  disableLoading(line) {
    // Réactiver l'interaction avec le panier
    const mainCartItems = document.querySelector(SELECTORS.CART_ITEMS);
    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    // Vérifier si c'est un item dans un bundle (dans le details du bundle)
    const bundlePackItem = this.querySelector(`#CartItem-${line}.cart-item__pack-item`);

    if (bundlePackItem) {
      // Pour les items du bundle, masquer le spinner et restaurer les contrôles
      const itemSpinner = bundlePackItem.querySelector('.cart-item__pack-item-loading');
      const removeButton = bundlePackItem.querySelector('cart-remove-button');
      const quantityWrapper = bundlePackItem.querySelector('.cart-item__pack-quantity-wrapper');

      if (itemSpinner) {
        itemSpinner.classList.add('hidden');
      }
      if (removeButton) {
        removeButton.style.visibility = 'visible';
      }
      if (quantityWrapper) {
        quantityWrapper.style.visibility = 'visible';
      }
    } else {
      // Masquer les spinners de chargement normaux
      const cartItemElements = this.querySelectorAll(`#CartItem-${line} ${SELECTORS.LOADING_SPINNER}`);
      const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} ${SELECTORS.LOADING_SPINNER}`);

      [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => {
        overlay.classList.add('hidden');
      });
    }
  }
}
// Enregistrer le composant personnalisé CartItems
customElements.define('cart-items', CartItems);

/**
 * Composant CartNote - Gère les notes de panier
 * Ce composant permet à l'utilisateur d'ajouter une note à sa commande
 * et l'envoie automatiquement au serveur après un délai
 */
if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        // Écouter les modifications de la note et les envoyer au serveur
        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
